<div class="new-setting-modal">
    <div class="modal-header">
        <h2>{{ isEditMode ? 'Edit Setting' : 'Create New Setting' }}</h2>
        <button aria-label="Close" class="close-button" type="button" (click)="onCancel()">
            <span aria-hidden="true">&times;</span>
        </button>
    </div>

    <form [formGroup]="settingForm" (ngSubmit)="onSubmit()">
        <div class="form-container">
            <!-- Name Field -->
            <div class="form-group">
                <label for="name">Name <span class="required">*</span></label>
                <input autocomplete="off" formControlName="name" id="name" placeholder="e.g., InsideLensEnabled"
                    type="text" [attr.readonly]="!isEditable || null" [class.invalid]="isFieldInvalid('name')">
                <small *ngIf="isFieldInvalid('name')" class="error-message">
                    <span *ngIf="settingForm.get('name')?.hasError('required')">Name is required</span>
                    <span *ngIf="settingForm.get('name')?.hasError('pattern')">Name must be in PascalCase format</span>
                    <span *ngIf="settingForm.get('name')?.hasError('duplicateName')">This setting name already
                        exists</span>
                </small>
                <small *ngIf="isEditMode" class="hint">Name cannot be changed when editing a setting</small>
            </div>

            <!-- Label Field -->
            <div class="form-group">
                <label for="label">Label <span class="required">*</span></label>
                <input autocomplete="off" formControlName="label" id="label" placeholder="Display name for the setting"
                    type="text" [attr.readonly]="!isEditable || null" [class.invalid]="isFieldInvalid('label')">
                <small *ngIf="isFieldInvalid('label')" class="error-message">
                    Label is required
                </small>
            </div>

            <!-- Category Field -->
            <div class="form-group">
                <label for="category">
                    Category <span class="required">*</span>
                    &nbsp;<span class="hint">Contact Device Platform team if you need a new category</span>
                </label>
                
                <select *ngIf="isEditable" formControlName="category" id="category"
                    [class.invalid]="isFieldInvalid('category')">
                    <option disabled value="">Select a category</option>
                    <option *ngFor="let cat of categoryOptions" [value]="cat">{{ cat }}</option>
                </select>
                <input *ngIf="!isEditable" formControlName="category" id="category" readonly type="text">
                <small *ngIf="isFieldInvalid('category')" class="error-message">
                    Category is required
                </small>
            </div>

            <!-- Class Field -->
            <div class="form-group">
                <label for="class">
                    Class <span class="required">*</span>
                    &nbsp;<span class="hint">If you're unsure which option to choose, ask your firmware dev.</span>
                </label>
                <select *ngIf="isEditable" formControlName="class" id="class">
                    <option *ngFor="let classOption of classOptions" [value]="classOption">
                        {{ classOption }}
                    </option>
                </select>
                <input *ngIf="!isEditable" formControlName="class" id="class" readonly type="text">
            </div>

            <!-- Data Type Field -->
            <div class="form-group">
                <label for="dataType">Data Type <span class="required">*</span></label>
                <select *ngIf="isEditable" formControlName="dataType" id="dataType">
                    <option *ngFor="let type of dataTypeOptions" [value]="type">
                        {{ type }}
                    </option>
                </select>
                <input *ngIf="!isEditable" formControlName="dataType" id="dataType" readonly type="text">
            </div>

            <!-- Default Value Field -->
            <div class="form-group">
                <label for="defaultValue">Default Value <span class="required">*</span></label>
                <input autocomplete="off" formControlName="defaultValue" id="defaultValue" placeholder="Default value"
                    type="text" [attr.readonly]="!isEditable || null" [class.invalid]="isFieldInvalid('defaultValue')">
                <small *ngIf="isFieldInvalid('defaultValue')" class="error-message">
                    Default value is required
                </small>
            </div>

            <!-- Min Value Field (Optional) -->
            <div *ngIf="isNumericDataType" class="form-group">
                <label for="minValue">Min Value (optional)</label>
                <input autocomplete="off" formControlName="minValue" id="minValue" placeholder="Minimum value"
                    type="text" [attr.readonly]="!isEditable || null">
            </div>

            <!-- Max Value Field (Optional) -->
            <div *ngIf="isNumericDataType" class="form-group">
                <label for="maxValue">Max Value (optional)</label>
                <input autocomplete="off" formControlName="maxValue" id="maxValue" placeholder="Maximum value"
                    type="text" [attr.readonly]="!isEditable || null">
            </div>

            <!-- Allowable Values Field (Optional) -->
            <div class="form-group allowable-values-section">
                <div class="allowable-values-header">
                    <label>Allowable Values (optional)</label>
                    <button *ngIf="isEditable" class="btn-add-value" type="button" (click)="addAllowableValue()">
                        + Add Value
                    </button>
                </div>
                <div class="allowable-values-list" cdkDropList formArrayName="allowableValues"
                    (cdkDropListDropped)="dropAllowableValue($event)">
                    <div *ngFor="let valueGroup of allowableValues.controls; let i = index" class="allowable-value-item"
                        cdkDrag [cdkDragDisabled]="!isEditable" [formGroupName]="i">
                        <div *cdkDragPreview class="allowable-value-drag-preview">
                            {{ valueGroup.get('label')?.value || valueGroup.get('value')?.value || 'Value' }}
                        </div>
                        <div class="allowable-value-inputs">
                            <button *ngIf="isEditable" cdkDragHandle class="btn-drag-handle" type="button"
                                aria-label="Drag to reorder" title="Drag to reorder">
                                &#x2630;
                            </button>
                            <div class="input-wrapper">
                                <input autocomplete="off" formControlName="label" placeholder="Label" type="text"
                                    [attr.readonly]="!isEditable || null">
                            </div>
                            <div class="input-wrapper">
                                <input autocomplete="off" formControlName="value" placeholder="Value" type="text"
                                    [attr.readonly]="!isEditable || null">
                            </div>
                            <button *ngIf="isEditable" class="btn-remove-value" type="button" (click)="removeAllowableValue(i)">
                                &times;
                            </button>
                        </div>
                    </div>
                </div>
                <small  *ngIf="isEditable && allowableValues.length === 0" class="hint">
                    Click "Add Value" to specify allowable values with labels
                </small>
            </div>

            <!-- Units Field (Optional) -->
            <div class="form-group">
                <label for="units">Units (optional)</label>
                <input autocomplete="off" formControlName="units" id="units" placeholder="e.g., meters, seconds"
                    type="text" [attr.readonly]="!isEditable || null">
            </div>

            <!-- Applicable Hardware Platforms Field (Optional) -->
            <div class="form-group">
                <label>Applicable Hardware Platforms (optional)</label>
                <div *ngIf="isEditable" class="platform-checkboxes">
                    <label class="checkbox-label">
                        <input formControlName="platformER" type="checkbox">
                        ER
                    </label>
                    <label class="checkbox-label">
                        <input formControlName="platformLytxOneHub" type="checkbox">
                        LytxOneHub
                    </label>
                    <label class="checkbox-label">
                        <input formControlName="platformAuxCam" type="checkbox">
                        AuxCam
                    </label>
                </div>
                <input *ngIf="!isEditable" [value]="platformDisplayValue" readonly type="text">
            </div>
        </div>

        <!-- Action Buttons -->
        <div *ngIf="!isEditable" class="button-group">
            <button class="btn-cancel" type="button" (click)="onCancel()">Close</button>
        </div>
        <div *ngIf="isEditable" class="button-group">
            <button class="btn-cancel" type="button" [disabled]="isSubmitting" (click)="onCancel()">Cancel</button>
            <button class="btn-submit" type="submit" [disabled]="settingForm.invalid || isSubmitting">
                <span *ngIf="!isSubmitting">{{ isEditMode ? 'Update Setting' : 'Create Setting' }}</span>
                <span *ngIf="isSubmitting" class="spinner-container">
                    <span class="spinner"></span>
                    {{ isEditMode ? 'Updating...' : 'Creating...' }}
                </span>
            </button>
        </div>
    </form>
</div>